/**
 * Teamspace SCOPE chooser — the shared list body, plus the one visibility predicate every shell
 * consumes (spec `26-spec-topnav-0826.md` §3/§4, T981/T983).
 *
 * Two shells render this exact list, so the [ALL] entry, its `SHOW_ALL_VIEW` /
 * `selectIsAllViewAvailable` gating and the ROOT→"Main Team" relabelling exist HERE only:
 *  - `TeamSwitcher`     — the Radix sub-menu shell (inside the profile menu / EmergencyTopBar)
 *  - `TeamScopeButton`  — the standalone top-bar shell (its own trigger + DropdownMenu)
 *
 * Radix sub-menu parts (`DropdownMenuSub*`) only render inside a parent `DropdownMenu`, which is
 * why the top-bar control cannot reuse the sub-menu shell — hence one list, two shells rather than
 * two copies of the list.
 *
 * `DropdownMenuItem` is valid in both a `DropdownMenuSubContent` and a `DropdownMenuContent`, so
 * the rows themselves need no per-shell branching. Search state lives here and resets for free:
 * Radix unmounts either content container when the menu closes.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search, Users } from 'lucide-react';
import { DropdownMenuItem, Input } from '@scio3/ui';
import { TruncatedTooltip } from '@/components/shared/TruncatedTooltip';
import { clearPersistedCache } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { selectIsAllTeamsScope, selectIsAllViewAvailable, useTeamStore } from '@/stores/team-store';
import { ALL_TEAMS_SCOPE } from '@/features/group-security/constants';
import { analyticsTeamSwitched } from '@/lib/analytics-service';
import { clearPersistedPaths } from '@/hooks/usePersistedPath';
import { useTeamNoun } from '@/hooks/useTeamNoun';
import { TeamspaceIcon } from '@/features/workspaces/components/TeamspaceIcon';

/** Below this many teamspaces the list fits at a glance, so the search row is hidden. */
const SEARCH_MIN_TEAMS = 6;

/** Thin trailing check marking the current scope (right-aligned, Notion-style). */
function SelectedCheck() {
  return <Check className="ml-auto size-4 shrink-0 text-foreground" strokeWidth={1.5} />;
}

interface TeamScopeGuardInput {
  /** The user's teamspace reach (`me.teams`) — only the count matters. */
  teams: readonly unknown[];
  /** `company.feature.allowTeams` — the plan check. */
  allowTeams?: boolean;
}

/**
 * Is a teamspace scope chooser worth showing at all?
 *
 * Pure `teams.length <= 1` (spec §4): the old guard also required the single team to be ROOT
 * (`teams[0]._id === '1'`), so a user whose ONLY teamspace was a non-root team still saw a chooser
 * with nothing to choose. Plan check (`allowTeams`) kept.
 *
 * THE one predicate — every shell (and `EmergencyTopBar`'s divider) calls it rather than restating
 * the condition, so the guard can never disagree with itself again.
 */
export function canSwitchTeamScope({ teams, allowTeams }: TeamScopeGuardInput): boolean {
  return !!allowTeams && teams.length > 1;
}

/** Store-bound form of `canSwitchTeamScope` — what components should use. */
export function useCanSwitchTeamScope(): boolean {
  const allowTeams = useAuthStore((s) => s.company?.feature?.allowTeams);
  const teams = useTeamStore((s) => s.teams);
  return canSwitchTeamScope({ teams, allowTeams });
}

/**
 * The label for the CURRENT scope — "All Teamspaces" in the [ALL] scope, else the active teamspace
 * name (ROOT's sentinel `'Default'` rendered as the Main-Team label). Shared so both shells'
 * triggers say the same thing.
 */
export function useTeamScopeLabel(): string {
  const { t } = useTranslation();
  const { noun } = useTeamNoun();
  const teams = useTeamStore((s) => s.teams);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const isAllTeamsScope = useTeamStore(selectIsAllTeamsScope);

  // FIXED string, NOT `useTeamNoun`'s plural: "Teamspaces" is the product term the settings nav
  // settled on after the v4 rename, while the hook's Team/Workspace split is the older vocabulary
  // (it still governs every OTHER label here, so it is deliberately not used for this one).
  if (isAllTeamsScope) return t('All Teamspaces');

  const activeTeam = teams.find((team) => team._id === activeTeamId);
  // M10 — the root team's backend name is the SENTINEL 'Default' (see open-api `_getRootTeam`),
  // never a display string. Render the Main Team label for it; the "Default" BADGE on the
  // Teamspaces row is a different key (`t('Default')`) and deliberately still says Default.
  if (activeTeam?.name === 'Default') return t('Default {{noun}}', { noun });
  return activeTeam?.name?.trim() || t('Default {{noun}}', { noun });
}

/**
 * Clear everything scoped to the old teamspace, then load `landingPath` — by default where you are,
 * minus query params (?id=, ?path=, ?teamId=), which would leak the old team's data across.
 */
function resetTeamScopedState(teamId: string, landingPath?: string) {
  window.dispatchEvent(new CustomEvent('team:switched', { detail: { teamId } }));
  // 1. Persisted folder paths (sessionStorage) — prevents restoring old team's folder
  clearPersistedPaths();
  // 2. Apollo cache (IndexedDB) — prevents stale data from previous team showing briefly
  clearPersistedCache();
  window.location.href = landingPath ?? window.location.pathname;
}

/**
 * Switch the active teamspace — THE switch every entry point uses (this list, and the Groups page's
 * Teamspaces menu), so analytics + cache/path clearing never diverge. `landingPath` sends the user
 * somewhere after the switch (e.g. Screens, to see that teamspace's content).
 */
export function useSwitchTeamspace() {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const setActiveTeam = useTeamStore((s) => s.setActiveTeam);
  return (teamId: string, landingPath?: string) => {
    analyticsTeamSwitched({ fromTeamId: activeTeamId ?? undefined, toTeamId: teamId });
    setActiveTeam(teamId);
    resetTeamScopedState(teamId, landingPath);
  };
}

export function TeamScopeList() {
  const { t } = useTranslation();
  const { noun, nounPluralLower } = useTeamNoun();
  const teams = useTeamStore((s) => s.teams);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const switchTeamspace = useSwitchTeamspace();
  const setTeamFilter = useTeamStore((s) => s.setTeamFilter);
  // [ALL] view (Group Security §1 / T901) — shown to EVERYONE when the flag is on and the user
  // reaches more than one teamspace. No role gate, no plan gate; flip SHOW_ALL_VIEW to remove it.
  const isAllViewAvailable = useTeamStore(selectIsAllViewAvailable);
  const isAllTeamsScope = useTeamStore(selectIsAllTeamsScope);
  const [search, setSearch] = useState('');

  // Same fixed "All Teamspaces" string as the trigger (`useTeamScopeLabel`) — see the note there
  // for why this one label does NOT ride `useTeamNoun`.
  const allLabel = t('All Teamspaces');
  const searchTerm = search.trim().toLowerCase();
  const filteredTeams = teams.filter((team) => {
    if (!searchTerm) return true;
    const name = team.name === 'Default' ? t('Default {{noun}}', { noun }) : team.name;
    return name.toLowerCase().includes(searchTerm);
  });
  const isSearchShown = teams.length > SEARCH_MIN_TEAMS;
  const showAllEntry =
    isAllViewAvailable && (!searchTerm || allLabel.toLowerCase().includes(searchTerm));

  /**
   * Enter the [ALL] scope. `activeTeamId` is deliberately left alone — it is the AUTH team (the
   * top-level `teamId` GraphQL arg), only the `query.teamId` FILTER is dropped. No analytics event:
   * `analyticsTeamSwitched` re-registers PostHog's `team_id` super property, and stamping 'all'
   * there would misattribute every later event.
   */
  function handleSwitchToAll() {
    setTeamFilter(ALL_TEAMS_SCOPE);
    resetTeamScopedState(ALL_TEAMS_SCOPE);
  }

  function handleSwitch(teamId: string) {
    switchTeamspace(teamId);
  }

  return (
    <>
      {/* Search only earns its row once the list is long enough to need it. */}
      {isSearchShown && (
        <div className="mx-2 mb-1 mt-2 flex items-center gap-2 rounded-md border px-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Search {{noun}}...', { noun: nounPluralLower })}
            aria-label={t('Search {{noun}}...', { noun: nounPluralLower })}
            className="h-8 border-none bg-transparent px-0 text-sm shadow-none focus-visible:ring-0"
            onKeyDown={(e) => e.stopPropagation()}
            data-testid="team-switcher-search"
          />
        </div>
      )}
      {/* Plain overflow div, NOT Radix ScrollArea: its viewport wraps children in a
          `display: table` box that grows to the longest name, so `truncate` never fires and the
          whole menu scrolls sideways. Rail hidden — the list still scrolls. */}
      <div
        className="max-h-60 overflow-y-auto overflow-x-hidden p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onWheel={(e) => e.stopPropagation()}
      >
        {/* [ALL] scope — FIRST entry, everyone, no role/plan gate (T901). */}
        {showAllEntry && (
          // Styled exactly like a teamspace row (Jessica, 2026-10-02) — first in the list is
          // emphasis enough. The glyph mirrors a picked teamspace icon (TeamspaceIcon, size sm).
          <DropdownMenuItem
            onClick={handleSwitchToAll}
            className="cursor-pointer gap-3"
            data-testid="team-switcher-all"
          >
            <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
              <Users className="size-4" />
            </span>
            <TruncatedTooltip text={allLabel} side="right" className="min-w-0" />
            {isAllTeamsScope && <SelectedCheck />}
          </DropdownMenuItem>
        )}
        {filteredTeams.length === 0 && !showAllEntry ? (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t('No {{noun}} found', { noun: nounPluralLower })}
          </div>
        ) : (
          filteredTeams.map((team) => {
            const teamLabel = team.name === 'Default' ? t('Default {{noun}}', { noun }) : team.name;
            return (
              <DropdownMenuItem
                key={team._id}
                onClick={() => handleSwitch(team._id)}
                // gap-3 + a 20px tile puts the name on the same line as the profile menu's labels
                // (16px icon + mr-2 + the item's gap-2), so the two header menus read alike.
                className="cursor-pointer gap-3"
                data-testid={`team-switcher-item-${team._id}`}
              >
                {/* The teamspace's picked icon + colour (or its first letter) on its 20px tile, as on
                    the Teamspaces page. */}
                <TeamspaceIcon name={teamLabel} icon={team.icon} color={team.iconColor} size="sm" />
                <TruncatedTooltip text={teamLabel} side="right" className="min-w-0" />
                {!isAllTeamsScope && team._id === activeTeamId && <SelectedCheck />}
              </DropdownMenuItem>
            );
          })
        )}
      </div>
    </>
  );
}
